<template>
	<div class="modal">
		<div class="row h-0">
			<div class="col-md-12 end-sm end-md end-lg">
				<a @click="close()"><span class="fa-light fa-circle-xmark textXlg textGrey"></span></a>
			</div>
		</div>
		<div class="pointer-events-none flex flex-col items-center justify-center gap-4 pt-4">
			<div class="col-md-12 mb-6 flex w-full items-center justify-center gap-4">
				<p class="textGrey textLg textCenter">
					Call Transcription
					<span class="font-bold">
						{{ moment(callData.startTime).format('DD/MM/YYYY HH:mm:ss') }}
					</span>
				</p>
			</div>
		</div>
		<div v-if="callData" class="flex flex-col items-center justify-center gap-4 pt-4">
			<div class="realtime-header">
				<p v-if="callData.leg" class="uppercase">
					<strong class="font-bold normal-case">Leg:</strong> {{ callData.leg }}
				</p>
				<p v-if="callData.a_call_from"><strong class="font-bold">From:</strong> {{ callData.a_call_from }}</p>
				<p v-if="callData.call_to"><strong class="font-bold">To:</strong> {{ callData.call_to }}</p>
				<p v-if="callData.a_duration">
					<strong class="font-bold">Duration:</strong>
					{{ realtime ? callData.connectedDuration : callData.a_duration }}s
				</p>
				<p v-if="callData.carrier_name">
					<strong class="font-bold">Carrier:</strong> {{ callData.carrier_name }}
				</p>
				<p v-if="callData.tenant_name">
					<strong class="font-bold">Tenant:</strong>
					{{ callData.tenant_name }}
				</p>
				<p v-if="callData.zone_name"><strong class="font-bold">Zone:</strong> {{ callData.zone_name }}</p>
			</div>
		</div>
		<div v-if="callData.transcription_summary" class="flex flex-col items-center justify-center gap-4 pt-14">
			<div class="realtime-header p-10">
				<div class="mb-4 w-full text-center text-lg font-semibold">Transcription Summary</div>
				<div class="w-full">{{ callData.transcription_summary }}</div>
			</div>
		</div>
		<div v-if="callData.transcription_summary" class="flex w-full flex-col items-center justify-center gap-4 pt-14">
			<div class="realtime-header w-full p-10">
				<div class="mb-4 w-full text-center text-lg font-semibold">Call Recording</div>
				<div class="mt-2 w-full"><media-player type="cdr" :file="callData" /></div>
			</div>
		</div>
		<div v-if="hasDiarization" class="flex w-full flex-col items-center justify-center gap-4 pt-14">
			<div class="realtime-header w-full p-10">
				<div class="mb-4 w-full text-center text-lg font-semibold">Diarized Transcription</div>
				<div class="chat-container">
					<div v-for="(message, index) in processedDiarizedMessages" :key="index" class="message-container">
						<div
							:class="[
								'message',
								getMessagePosition(message.speaker_id),
								getMessageColorClass(message.speaker_id)
							]"
						>
							<div class="message-header">{{ getSpeakerName(message.speaker_id) }}</div>
							<div class="message-content">{{ message.text }}</div>
							<div class="message-time">{{ formatMessageTime(message.start) }}</div>
						</div>
					</div>
				</div>
			</div>
		</div>
		<div v-if="!hasDiarization" class="flex w-full flex-col items-center justify-center gap-4 pt-14">
			<div class="realtime-header w-full p-10">
				<div class="mb-4 w-full text-center text-lg font-semibold">Full transcription text</div>
				<div>{{ callData.transcription }}</div>
			</div>
		</div>
		<div
			v-if="!callData.transcription_summary"
			class="flex w-full flex-col items-center justify-center gap-4 pt-14"
		>
			<div class="realtime-header w-full p-10">
				<div class="mt-2 w-full">
					<div class="mb-4 w-full text-center text-lg font-semibold">Call Recording</div>
					<media-player type="cdr" :file="callData" />
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import moment from 'moment'

export default {
	inject: ['user', 'userRoles'],
	props: ['callData', 'refreshInterval', 'realtime'],

	data() {
		return {
			moment,
			hasDiarization: false,
			diarizedData: {
				language_code: 'en',
				language_probability: 1,
				text: '',
				words: []
			}
		}
	},
	computed: {
		processedDiarizedMessages() {
			if (!this.diarizedData || !this.diarizedData.words) return []

			const messages = []
			let currentMessage = null

			this.diarizedData.words.forEach((word) => {
				if (word.type === 'word') {
					if (!currentMessage || currentMessage.speaker_id !== word.speaker_id) {
						if (currentMessage) {
							messages.push(currentMessage)
						}

						currentMessage = {
							text: word.text,
							start: word.start,
							end: word.end,
							speaker_id: word.speaker_id
						}
					} else {
						currentMessage.text += word.text
						currentMessage.end = word.end
					}
				} else if (word.type === 'spacing' && currentMessage) {
					currentMessage.text += ' '
				}
			})

			if (currentMessage) {
				messages.push(currentMessage)
			}

			return messages
		}
	},
	mounted() {
		document.addEventListener('keyup', this.onKeyUp)
	},
	beforeUnmount() {
		document.removeEventListener('keyup', this.onKeyUp)
	},

	methods: {
		close() {
			console.log('close')
			this.$emit('close')
		},
		onKeyUp(event) {
			if (event.key == 'Escape') this.$emit('close')
		},
		getMessagePosition(speakerId) {
			const speakerNumber = parseInt(speakerId.split('_')[1])
			return speakerNumber % 2 === 0 ? 'message-left' : 'message-right'
		},
		getMessageColorClass(speakerId) {
			const speakerNumber = parseInt(speakerId.split('_')[1])
			switch (speakerNumber) {
				case 0:
					return 'message-accent'
				case 1:
					return 'message-blue'
				case 2:
					return 'message-accent-light'
				case 3:
					return 'message-blue-light'
				default:
					return 'message-accent'
			}
		},
		getSpeakerName(speakerId) {
			const speakerNumber = parseInt(speakerId.split('_')[1]) + 1
			return `Speaker ${speakerNumber}`
		},
		formatMessageTime(seconds) {
			const minutes = Math.floor(seconds / 60)
			const secs = Math.floor(seconds % 60)
			return `${minutes}:${secs.toString().padStart(2, '0')}`
		}
	}
}
</script>

<style scoped>
::-webkit-scrollbar {
	width: 0px !important;
}

.realtime-header {
	@apply flex flex-wrap items-center justify-center gap-x-8 gap-y-4 rounded-lg bg-white p-4 text-black dark:bg-offmain dark:text-white;
}

.realtime-header p {
	@apply whitespace-nowrap rounded-sm bg-black/5 px-4 py-2 text-sm font-semibold dark:bg-white/5;
}

.chat-container {
	@apply flex w-full flex-col gap-3 overflow-y-auto;
	max-height: 500px;
}

.message-container {
	@apply flex w-full flex-col;
	margin-bottom: 12px;
}

.message {
	@apply flex max-w-[70%] flex-col rounded-lg px-4 py-2 shadow-sm;
}

.message-left {
	@apply self-start;
	border-radius: 0 12px 12px 12px;
}

.message-right {
	@apply self-end;
	border-radius: 12px 0 12px 12px;
}

.message-header {
	@apply mb-1 text-sm font-semibold;
}

.message-content {
	@apply text-sm;
}

.message-time {
	@apply mt-1 text-right text-xs opacity-60;
}

.message-accent {
	@apply bg-accentMain text-black;
}

.message-blue {
	@apply bg-blue text-white;
}

.message-accent-light {
	@apply bg-accentLight text-black;
}

.message-blue-light {
	@apply bg-blue-200 text-blue-900;
}
</style>
